/**
 * Clinic portal clinic location hooks.
 * All API calls go through @/services/main/clinicLocations.
 * Tenant/clinic from useClinicContext; X-Service-Type via useClinicPortalServiceType
 * (reactive, so the list reloads when the user toggles A↔R — HIT-4650).
 * Query errors surfaced via toast.
 */

import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, isNotFoundError, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import {
  getClinicLocationsList,
  getClinicLocationById,
} from '@/services/main/clinicLocations';
import type { ClinicLocationSearchParams } from '@/pages/main/clinicLocation/types';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

/**
 * Hook to get paginated list of clinic locations with error handling.
 */
export const useMainClinicLocations = (
  params?: ClinicLocationSearchParams,
  options?: { enabled?: boolean }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery({
    queryKey: ['clinic-locations', 'list', tenant, clinicNumber, serviceType, params],
    queryFn: () => getClinicLocationsList(tenant, clinicNumber, params, serviceType),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};

/**
 * Hook to get a single clinic location by ID.
 */
export const useMainClinicLocation = (id: number) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['clinic-locations', 'detail', tenant, clinicNumber, serviceType, id],
    queryFn: () => getClinicLocationById(tenant, clinicNumber, id, serviceType),
    enabled: !!tenant && !!clinicNumber && !!id,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error && !isNotFoundError(error)) {
      toast.error(getApiErrorMessage(error, '拠点情報の取得に失敗しました。'));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
};



